/**
 * MetaLeadsTab - "Meta Connect" validation queue.
 * Performance Engineers / Admins sync leads from Meta Lead Ads, then approve
 * (auto-assign to least-loaded sales, or pick a rep) or reject with a reason.
 * Unvalidated leads never reach the sales CRM until approved here.
 */
import { useState, useEffect, useCallback } from 'react';
import { metaLeadsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Check, X, Facebook, Inbox, ShieldAlert, CheckCircle2, Clock, BarChart3, TrendingUp, Send, Settings, Users } from 'lucide-react';

const STATUS_MESSAGES = {
  no_token: { label: 'Not Connected', desc: 'No Meta access token is configured yet.', tone: 'muted' },
  invalid_token: { label: 'Token Invalid / Expired', desc: 'The Meta access token is invalid or expired. Provide a fresh token to enable syncing.', tone: 'destructive' },
  missing_permissions: { label: 'Missing Permissions', desc: 'The token is valid but lacks the required lead-retrieval permissions.', tone: 'destructive' },
  page_not_assigned: { label: 'Page Not Assigned', desc: 'Token is valid with the right permissions, but the System User can\'t access the Vibe6 Digital LLP Page yet. Assign the Page to the System User with "Manage Page" + "Leads Access" in Meta Business Settings.', tone: 'destructive' },
  meta_unreachable: { label: 'Meta Unreachable', desc: 'Could not reach Meta Graph API. Try again shortly.', tone: 'destructive' },
  meta_error: { label: 'Meta Error', desc: 'Meta returned an error while checking the connection.', tone: 'destructive' },
};

function fmtTime(iso) {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

export default function MetaLeadsTab() {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [view, setView] = useState('pending'); // pending | rejected
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [salesUsers, setSalesUsers] = useState([]);
  const [actioningId, setActioningId] = useState(null);
  const [assignChoice, setAssignChoice] = useState({}); // leadId -> salesUserId | 'auto'

  const [rejectDialog, setRejectDialog] = useState({ open: false, lead: null });
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const [insights, setInsights] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [reachData, setReachData] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);

  const [configOpen, setConfigOpen] = useState(false);
  const [configEntities, setConfigEntities] = useState([]);
  const [tokenInputs, setTokenInputs] = useState({});
  const [datasetInputs, setDatasetInputs] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);
  const [configResults, setConfigResults] = useState(null);

  const openConfig = async () => {
    setConfigResults(null);
    setTokenInputs({});
    setConfigOpen(true);
    try {
      const { data } = await metaLeadsAPI.getConfig();
      const entities = data.entities || [];
      setConfigEntities(entities);
      setDatasetInputs(Object.fromEntries(entities.map((e) => [e.key, e.capi_dataset_id || ''])));
    } catch {
      toast.error('Failed to load Meta configuration');
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    setConfigResults(null);
    try {
      const accounts = configEntities.map((e) => ({
        key: e.key,
        label: e.label,
        token: tokenInputs[e.key] ?? '',   // blank => keep existing on backend
        page_ids: e.page_ids || [],
        capi_dataset_id: (datasetInputs[e.key] ?? '').trim(),
      }));
      const { data } = await metaLeadsAPI.saveConfig(accounts);
      setConfigResults(data.results || []);
      const ok = (data.results || []).some((r) => r.valid);
      if (ok) toast.success('Meta tokens saved & validated');
      else toast.warning('Saved, but check the validation messages');
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await metaLeadsAPI.sendRejectionDigest();
      if (data.sent) {
        toast.success(`Digest emailed to ${data.recipients?.length || 0} recipient(s)`);
      } else if (data.reason === 'no_rejections') {
        toast.info('No rejected leads in the last 7 days — nothing to send.');
      } else {
        toast.error(data.message || 'Could not send digest');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const { data } = await metaLeadsAPI.getStatus();
      setStatus(data);
    } catch {
      setStatus({ ready: false, reason: 'meta_error' });
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const loadLeads = useCallback(async (state) => {
    setLoadingLeads(true);
    try {
      const { data } = await metaLeadsAPI.getLeads(state);
      setLeads(data.leads || []);
    } catch {
      toast.error('Failed to load Meta leads');
    } finally {
      setLoadingLeads(false);
    }
  }, []);

  const loadSalesUsers = useCallback(async () => {
    try {
      const { data } = await metaLeadsAPI.getSalesUsers();
      setSalesUsers(data.users || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadStatus(); loadSalesUsers(); }, [loadStatus, loadSalesUsers]);
  useEffect(() => {
    if (view === 'insights') {
      setLoadingInsights(true);
      Promise.all([metaLeadsAPI.getInsights(), metaLeadsAPI.getCampaignQuality(), metaLeadsAPI.getCampaignReach(30)])
        .then(([ins, camp, reach]) => {
          setInsights(ins.data);
          setCampaigns(camp.data.campaigns || []);
          setReachData(reach.data || null);
        })
        .catch(() => toast.error('Failed to load insights'))
        .finally(() => setLoadingInsights(false));
    } else {
      loadLeads(view);
    }
  }, [view, loadLeads]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await metaLeadsAPI.sync();
      if (data.ok) {
        toast.success(data.message || 'Sync complete');
        await Promise.all([loadStatus(), loadLeads(view)]);
      } else {
        toast.error(data.message || 'Sync failed');
        await loadStatus();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleApprove = async (lead) => {
    const choice = assignChoice[lead.id] || 'auto';
    setActioningId(lead.id);
    try {
      const { data } = await metaLeadsAPI.approve(lead.id, choice === 'auto' ? null : choice);
      toast.success(`Approved — assigned to ${data.assigned_to_name || 'sales'}`);
      setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    } finally {
      setActioningId(null);
    }
  };

  const openReject = (lead) => { setRejectReason(''); setRejectDialog({ open: true, lead }); };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('A rejection reason is required'); return; }
    setRejecting(true);
    try {
      await metaLeadsAPI.reject(rejectDialog.lead.id, rejectReason.trim());
      toast.success('Lead rejected');
      setLeads((prev) => prev.filter((l) => l.id !== rejectDialog.lead.id));
      setRejectDialog({ open: false, lead: null });
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rejection failed');
    } finally {
      setRejecting(false);
    }
  };

  const sm = status && !status.ready ? STATUS_MESSAGES[status.reason] : null;

  return (
    <div className="space-y-4" data-testid="meta-leads-tab">
      {/* Connection status + sync */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#1877F2]/10 flex items-center justify-center shrink-0">
                <Facebook className="w-5 h-5 text-[#1877F2]" />
              </div>
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  Meta Connect
                  {loadingStatus ? (
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  ) : status?.ready ? (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100" data-testid="meta-status-ready">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
                    </Badge>
                  ) : (
                    <Badge variant="destructive" data-testid="meta-status-error">
                      <ShieldAlert className="w-3 h-3 mr-1" /> {sm?.label || 'Not Ready'}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="mt-1">
                  {status?.ready
                    ? 'Sync incoming Lead Ads, then approve or reject each before it enters the CRM.'
                    : (sm?.desc || 'Checking Meta connection…')}
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              onClick={openConfig}
              data-testid="meta-configure-button"
              className="shrink-0"
            >
              <Settings className="w-4 h-4 mr-2" /> Configure
            </Button>
            <Button
              onClick={handleSync}
              disabled={syncing}
              data-testid="meta-sync-button"
              className="shrink-0"
            >
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              {syncing ? 'Syncing…' : 'Sync from Meta'}
            </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {status?.accounts?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4" data-testid="meta-accounts">
              {status.accounts.map((a) => (
                <div
                  key={a.key}
                  data-testid={`meta-account-${a.key}`}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${a.ready ? 'border-green-200 bg-green-50 text-green-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}
                >
                  {a.ready ? <CheckCircle2 className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
                  <span className="font-medium">{a.label}</span>
                  <span className="opacity-70">
                    {a.ready ? 'Connected' : (STATUS_MESSAGES[a.reason]?.label || a.reason || 'Not ready')}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => setView('pending')}
              data-testid="meta-view-pending"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${view === 'pending' ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:bg-muted/50'}`}
            >
              <Clock className="w-4 h-4" /> Pending
              <Badge variant="outline" className="ml-1">{status?.pending ?? 0}</Badge>
            </button>
            <button
              onClick={() => setView('rejected')}
              data-testid="meta-view-rejected"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${view === 'rejected' ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:bg-muted/50'}`}
            >
              <X className="w-4 h-4" /> Rejected
              <Badge variant="outline" className="ml-1">{status?.rejected ?? 0}</Badge>
            </button>
            <button
              onClick={() => setView('insights')}
              data-testid="meta-view-insights"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${view === 'insights' ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:bg-muted/50'}`}
            >
              <BarChart3 className="w-4 h-4" /> Insights
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Insights view */}
      {view === 'insights' ? (
        <div className="space-y-4" data-testid="meta-insights">
          {loadingInsights ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          ) : (
            <>
              {/* Funnel totals */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Total Meta Leads', value: insights?.totals?.total ?? 0, cls: 'text-foreground' },
                  { label: 'Pending', value: insights?.totals?.pending ?? 0, cls: 'text-amber-600' },
                  { label: 'Approved', value: insights?.totals?.approved ?? 0, cls: 'text-green-600' },
                  { label: 'Rejected', value: insights?.totals?.rejected ?? 0, cls: 'text-red-600' },
                ].map((c) => (
                  <Card key={c.label}>
                    <CardContent className="p-4">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">{c.label}</p>
                      <p className={`text-2xl font-bold mt-1 ${c.cls}`}>{c.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Campaign performance */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Campaign Quality</CardTitle>
                      <CardDescription>
                        Approval rate per Meta campaign (from validated leads)
                        {reachData?.enabled ? ` · Reach = unique people, last ${reachData.window_days}d` : ''}
                      </CardDescription>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleSendDigest}
                      disabled={sendingDigest}
                      data-testid="meta-send-digest"
                    >
                      {sendingDigest ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                      Email rejection digest
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {campaigns.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">No Meta campaign data yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {!reachData?.enabled && (
                        <div className="text-xs rounded-md border border-dashed border-border bg-muted/40 px-3 py-2 text-muted-foreground" data-testid="meta-reach-disabled">
                          {reachData?.reason === 'missing_ads_read'
                            ? 'Reach unavailable — grant the Meta token the ads_read permission to show unique reach per campaign.'
                            : reachData?.reason === 'no_ad_id'
                            ? 'Reach unavailable — re-sync leads so each lead carries its ad_id, then reach can be fetched.'
                            : 'Reach unavailable for now.'}
                        </div>
                      )}
                      {(() => {
                        const reachByCampaign = Object.fromEntries((reachData?.campaigns || []).map((r) => [r.campaign, r]));
                        return campaigns.map((c) => {
                          const decided = c.approved + c.rejected;
                          const rate = decided ? Math.round((c.approved / decided) * 100) : 0;
                          const rr = reachByCampaign[c.campaign];
                          return (
                            <div key={c.campaign} data-testid={`meta-campaign-${c.campaign}`}>
                              <div className="flex items-center justify-between text-sm mb-1">
                                <span className="font-medium truncate mr-2">{c.campaign}</span>
                                <span className="text-muted-foreground shrink-0 flex items-center gap-2">
                                  {rr && rr.reach != null && (
                                    <span className="inline-flex items-center gap-1 font-medium text-foreground" data-testid={`meta-reach-${c.campaign}`}>
                                      <Users className="w-3.5 h-3.5" /> {rr.reach.toLocaleString()} reach
                                    </span>
                                  )}
                                  <span>{c.approved}✓ · {c.rejected}✕ · {c.pending} pending · {c.total} total</span>
                                </span>
                              </div>
                              <div className="h-2 w-full rounded-full bg-muted overflow-hidden flex">
                                <div className="h-full bg-green-500" style={{ width: `${decided ? (c.approved / c.total) * 100 : 0}%` }} />
                                <div className="h-full bg-red-400" style={{ width: `${decided ? (c.rejected / c.total) * 100 : 0}%` }} />
                                <div className="h-full bg-amber-300" style={{ width: `${c.total ? (c.pending / c.total) * 100 : 0}%` }} />
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {rate}% approval rate
                                {rr && rr.reach != null && rr.impressions != null ? ` · ${rr.impressions.toLocaleString()} impressions` : ''}
                              </p>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Rejection reasons */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2"><X className="w-4 h-4" /> Rejection Reasons</CardTitle>
                  <CardDescription>Why leads were rejected — feedback for the ads team</CardDescription>
                </CardHeader>
                <CardContent>
                  {(insights?.rejection_reasons?.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">No rejected leads yet.</p>
                  ) : (
                    <div className="divide-y">
                      {insights.rejection_reasons.map((r, i) => (
                        <div key={i} className="flex items-center justify-between py-2 text-sm" data-testid={`meta-reason-${i}`}>
                          <span className="truncate mr-2">{r.reason}</span>
                          <Badge variant="outline">{r.count}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      ) : (
      /* Leads list */
      <Card>
        <CardContent className="p-0">
          {loadingLeads ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground" data-testid="meta-empty-state">
              <Inbox className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">{view === 'pending' ? 'No leads awaiting validation' : 'No rejected leads'}</p>
              <p className="text-sm">{view === 'pending' ? 'Click “Sync from Meta” to pull new Lead Ads.' : 'Rejected leads will appear here.'}</p>
            </div>
          ) : (
            <div className="divide-y">
              {leads.map((lead) => (
                <div key={lead.id} className="p-4 hover:bg-muted/40 transition-colors" data-testid={`meta-lead-${lead.id}`}>
                  <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{lead.name || 'Unknown'}</span>
                        {lead.meta_campaign_name && (
                          <Badge variant="outline" className="text-xs">{lead.meta_campaign_name}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {[lead.email, lead.phone, lead.company].filter(Boolean).join(' • ') || 'No contact details'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {lead.meta_form_name ? `${lead.meta_form_name} • ` : ''}Received {fmtTime(lead.meta_created_time)}
                      </p>
                      {view === 'rejected' && lead.rejection_reason && (
                        <p className="text-xs text-red-600 mt-1">Reason: {lead.rejection_reason}</p>
                      )}
                    </div>

                    {view === 'pending' && (
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
                        <Select
                          value={assignChoice[lead.id] || 'auto'}
                          onValueChange={(v) => setAssignChoice((p) => ({ ...p, [lead.id]: v }))}
                        >
                          <SelectTrigger className="w-full sm:w-[190px]" data-testid={`meta-assign-select-${lead.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="auto">Auto (least-loaded)</SelectItem>
                            {salesUsers.map((u) => (
                              <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          onClick={() => handleApprove(lead)}
                          disabled={actioningId === lead.id}
                          data-testid={`meta-approve-${lead.id}`}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {actioningId === lead.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openReject(lead)}
                          data-testid={`meta-reject-${lead.id}`}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <X className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Configure tokens dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-lg" data-testid="meta-config-dialog">
          <DialogHeader>
            <DialogTitle>Configure Meta Tokens</DialogTitle>
            <DialogDescription>
              Paste each entity's System User access token. Saved securely in the app — no redeploy needed.
              Leave a field blank to keep the existing token.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
            {configEntities.map((e) => {
              const res = configResults?.find((r) => r.key === e.key);
              return (
                <div key={e.key} className="space-y-2" data-testid={`meta-config-entity-${e.key}`}>
                  <div className="flex items-center justify-between">
                    <Label className="font-semibold">{e.label}</Label>
                    {e.has_token && (
                      <span className="text-xs text-muted-foreground">current: {e.token_masked}</span>
                    )}
                  </div>
                  <Textarea
                    rows={3}
                    value={tokenInputs[e.key] ?? ''}
                    onChange={(ev) => setTokenInputs((p) => ({ ...p, [e.key]: ev.target.value }))}
                    placeholder={e.has_token ? 'Paste a new token to replace, or leave blank to keep current' : 'Paste the System User access token'}
                    className="font-mono text-xs"
                    data-testid={`meta-config-token-${e.key}`}
                  />
                  {res && (
                    <div className={`text-xs flex items-center gap-1 ${res.valid ? 'text-green-600' : 'text-red-600'}`} data-testid={`meta-config-result-${e.key}`}>
                      {res.valid ? <CheckCircle2 className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
                      {res.valid ? 'Valid — all required permissions present' : `Issue: ${STATUS_MESSAGES[res.reason]?.label || res.reason}`}
                    </div>
                  )}
                  <div className="space-y-1 pt-1">
                    <Label className="text-xs text-muted-foreground">Conversions API dataset ID (optional — enables CRM→Meta stage sync)</Label>
                    <Input
                      value={datasetInputs[e.key] ?? ''}
                      onChange={(ev) => setDatasetInputs((p) => ({ ...p, [e.key]: ev.target.value }))}
                      placeholder="e.g. 123456789012345"
                      className="font-mono text-xs"
                      data-testid={`meta-config-dataset-${e.key}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigOpen(false)} data-testid="meta-config-cancel">Close</Button>
            <Button onClick={handleSaveConfig} disabled={savingConfig} data-testid="meta-config-save">
              {savingConfig ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save &amp; Validate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(o) => setRejectDialog({ open: o, lead: o ? rejectDialog.lead : null })}>
        <DialogContent data-testid="meta-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject Lead</DialogTitle>
            <DialogDescription>
              Rejecting “{rejectDialog.lead?.name}”. A reason is required and the lead will not enter the CRM.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-reason">Rejection reason *</Label>
            <Textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Spam / duplicate / invalid contact details"
              data-testid="meta-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, lead: null })} data-testid="meta-reject-cancel">Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejecting || !rejectReason.trim()}
              data-testid="meta-reject-confirm"
            >
              {rejecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Reject Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
